import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { form, required, FormField, pattern, validate } from '@angular/forms/signals';
import { RouterModule } from '@angular/router';
import { AuthApiService } from '@core/services/api/auth.service';

interface IMensajeFront {
  success: boolean;
  message: string;
}

@Component({
  selector: 'app-auth-reset-password',
  templateUrl: './reset-password.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterModule, FormField, CommonModule],
})
export class AuthResetPassword {
  private readonly authApiService = inject(AuthApiService);

  showPassword = signal(false);
  mensajeFront = signal<IMensajeFront | null>(null);
  email = input.required<string>();
  token = input.required<string>();
  contraCambioExitoso = signal(false);

  passwordStrength = computed(() => {
    let score = 0;
    const password = this.form().value().newPassword;
    if (!password) return 0;

    // Criterios
    if (password.length >= 8) score++; // largo mínimo
    if (password.match(/[A-Z]/)) score++; // mayúsculas
    if (password.match(/[a-z]/)) score++; // minúsculas
    if (password.match(/[0-9]/)) score++; // números
    if (password.match(/[^A-Za-z0-9]/)) score++; // símbolos

    // Ajuste: máximo 4 barras, así que limitamos
    return Math.min(score, 4);
  });
  // Color por nivel (opcional para las barras)
  strengthColor = computed(() => {
    const score = this.passwordStrength();
    if (score <= 1) return 'bg-red-500';
    if (score === 2) return 'bg-orange-500';
    if (score === 3) return 'bg-yellow-500';
    return 'bg-green-500';
  });

  form = form(
    signal({
      newPassword: '',
      confirmPassword: '',
    }),
    (schemaPath) => {
      required(schemaPath.newPassword, { message: 'La nueva contraseña es obligatoria' });
      required(schemaPath.confirmPassword, {
        message: 'La confirmación de contraseña es obligatoria',
      });

      // Al menos una mayúscula
      pattern(schemaPath.newPassword, /[A-Z]/, {
        message: 'Debe contener al menos una mayúscula',
      });

      // Al menos una minúscula
      pattern(schemaPath.newPassword, /[a-z]/, {
        message: 'Debe contener al menos una minúscula',
      });

      // Al menos un número
      pattern(schemaPath.newPassword, /[0-9]/, {
        message: 'Debe contener al menos un número',
      });

      // (Opcional) al menos un símbolo
      pattern(schemaPath.newPassword, /[^A-Za-z0-9]/, {
        message: 'Debe contener al menos un símbolo',
      });

      // --- Validaciones para confirmPassword ---
      required(schemaPath.confirmPassword, {
        error: {
          kind: 'required',
          message: 'La confirmación de contraseña es obligatoria',
        },
      });

      // comparador de contraseñas
      validate(schemaPath.confirmPassword, (c) => {
        if (c.value() !== this.form().value().newPassword) {
          return {
            kind: 'confirmPasswordMismatch',
            path: 'confirmPassword',
            message: 'Las contraseñas no coinciden',
          };
        }
        return null;
      });
    },
  );

  async onSubmit(event: Event) {
    event.preventDefault();

    if (this.form().invalid() == true) {
      console.error(
        'Errores de validación:',
        this.form.newPassword().errors(),
        this.form.confirmPassword().errors(),
      );
      this.mensajeFront.set({
        success: false,
        message:
          this.form
            .newPassword()
            .errors()
            ?.map((e) => e.message)
            .join(', ') ||
          this.form
            .confirmPassword()
            .errors()
            ?.map((e) => e.message)
            .join(', ') ||
          'Por favor, corrige los errores en el formulario.',
      });
      return;
    }

    const { newPassword } = this.form().value();
    this.authApiService.resetPassword(this.email(), this.token(), newPassword).subscribe({
      next: (rs) => {
        if (rs.success) {
          this.contraCambioExitoso.set(true);
          console.log('Contraseña restablecida exitosamente');
          this.mensajeFront.set({
            success: true,
            message: rs.message || 'Contraseña restablecida exitosamente',
          });
        } else {
          this.mensajeFront.set({
            success: false,
            message: rs.message || 'Error al restablecer la contraseña',
          });
        }
      },
      error: (err) => {
        this.contraCambioExitoso.set(false);
        console.error('Error al restablecer la contraseña:', err);
        this.mensajeFront.set({
          success: false,
          message:
            err.error?.message ||
            'Ocurrió un error al intentar restablecer la contraseña. Por favor, intenta nuevamente.',
        });
      },
    });
  }
  togglePasswordVisibility() {
    this.showPassword.update((value) => !value);
  }
}
